// Devices — the installs this person is signed in on, and what each one has agreed to.
//
// Its own section beside Sessions, not folded into it. A session is a login you can
// revoke; a device is the hardware and its permissions — which is what you open when
// someone says they are not getting notifications. They answer different questions and
// only one of them has a revoke button.
//
// The row is bespoke rather than a `Row`: it needs a leading platform glyph, three lines
// instead of two, and a click target. Threading three more props into `Row` for one
// caller would make the shared component worse to read for the other nine.
import { useCallback, useEffect, useRef, useState } from 'react';
import { AndroidIcon, AppleIcon, CloseIcon, PhoneIcon } from '../../../ui/icons';
import { hairline, scrollY, softFill } from '../../../ui/styles';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from '../../../ui/core';
import { useFormatters } from '../../../hooks';
import api from '../../../lib/api';
import { SectionCard, EmptyRows, SectionLabel } from '../../../pages/DriverProfile/components';
import { TrailingMeta, Meta, SectionSkeleton, SectionError } from '../../../pages/UserProfile/parts';

/** iOS, Android, or an install that never said. */
const PlatformIcon = ({ platform, sx }) => {
  const key = String(platform || '').toLowerCase();
  if (key.includes('ios') || key.includes('apple')) return <AppleIcon sx={sx} />;
  if (key.includes('android')) return <AndroidIcon sx={sx} />;
  // A generic handset rather than one of the two: guessing a platform from a blank field
  // would be a claim, and this column is read as fact.
  return <PhoneIcon sx={sx} />;
};

/**
 * How a permission status should read.
 *
 * Only `denied` is coloured. `granted` is the expected state and does not need to shout,
 * and `undetermined` is "never been asked", which is not a problem — it is the state every
 * install starts in.
 */
const permissionColor = (status) => {
  if (status === 'denied') return 'error.main';
  if (status === 'granted') return 'success.main';
  return 'text.disabled';
};

/** `notifications` -> `Notifications`, `locationBackground` -> `Location background`. */
const humanizePermission = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

/** `PHONE` -> `Phone`. expo-device reports the type upper-cased on Android. */
const humanizeType = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : null);

/**
 * The OS, short enough to sit on one line.
 *
 * `osName` is "iOS" on Apple and a BUILD FINGERPRINT on Android —
 * `samsung/dm1qsqw/dm1q:16/BP4A.251205.006/S911USQU7FZE2:user/release-keys`, which wrapped
 * to three lines in the dialog and would have run off the row. Anything that long is not a
 * name, so the platform plus the version is used instead and the fingerprint moves to its
 * own field, where a long value is expected.
 *
 * The length test rather than a check for `platform === 'android'`: what makes the string
 * unusable here is its length, and iOS is one vendor away from reporting something similar.
 */
const OS_NAME_MAX = 24;

const osSummary = (device) => {
  const name =
    device.osName && device.osName.length <= OS_NAME_MAX
      ? device.osName
      : device.platform === 'android'
        ? 'Android'
        : device.platform || null;
  return [name, device.osVersion].filter(Boolean).join(' ') || null;
};

/** The fingerprint, only when it is not already what `osSummary` showed. */
const osBuild = (device) => (device.osName && device.osName.length > OS_NAME_MAX ? device.osName : null);

/**
 * The permission list, falling back to the one field an older API version served.
 *
 * `permissions` arrived after `notificationPermission` did (hitch-services #3453), so a
 * response from a backend that predates it has the notification status and nothing else.
 * Rendering "never reported a permission snapshot" in that case is a lie about the device
 * rather than about the API — the snapshot exists, this deployment just cannot show it.
 */
const PERMISSION_ORDER = ['denied', 'undetermined', 'granted'];

const permissionEntries = (device) => {
  const entries = Object.entries(device.permissions ?? {});
  const known = entries.length
    ? entries
    : device.notificationPermission
      ? [['notifications', device.notificationPermission]]
      : [];

  // Anything needing action first, then alphabetical inside each state — so two devices
  // list their permissions in the same order and a denied one is never buried under five
  // granted ones. An unrecognised status sorts with the actionable states rather than last:
  // a value nobody has seen before is worth noticing.
  return [...known].sort(([leftKey, leftStatus], [rightKey, rightStatus]) => {
    const rank = (status) => {
      const i = PERMISSION_ORDER.indexOf(status);
      return i === -1 ? 0 : i;
    };
    return rank(leftStatus) - rank(rightStatus) || leftKey.localeCompare(rightKey);
  });
};

/**
 * Every field on the device, for when the summary row is not enough.
 *
 * The row can carry three or four facts; the table has fourteen, including the ids you
 * need to correlate an install with a log line. The dialog is where they go rather than a
 * wider row — this section sits in a 420px-minimum column.
 */
const DeviceDialog = ({ device, onClose }) => {
  const f = useFormatters();
  if (!device) return null;

  const at = (value) => (value ? f.date.toLocaleString(value, f.date.DateFormats.DATETIME_MED) : '—');
  const permissions = permissionEntries(device);

  const facts = [
    ['Device id', device.deviceId],
    ['Platform', device.platform],
    ['Model', device.modelName],
    ['Brand', device.brand],
    // Dropped when it says the same thing as the brand, which on most handsets it does
    // ("samsung" / "samsung"). A row that repeats its neighbour is one more thing to read.
    device.manufacturer && device.manufacturer !== device.brand && ['Manufacturer', device.manufacturer],
    ['Type', humanizeType(device.deviceType)],
    ['OS', osSummary(device)],
    ['App version', device.appVersion],
    ['App build', device.buildVersion],
    // Stated both ways round: "Physical: no" is easy to skim past when chasing a push bug
    // that turns out to be a simulator.
    ['Hardware', device.isPhysical === false ? 'Simulator' : device.isPhysical === true ? 'Physical device' : null],
    ['Seen at', at(device.lastSeenAt)],
    ['First registered', at(device.firstSeenAt)],
    // Full width and last: Android's is a 60-character fingerprint, so it gets the row
    // rather than half of one.
    osBuild(device) && ['OS build', osBuild(device), 'wide'],
  ].filter(Boolean);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 3, pr: 1.5, pt: 2 }}>
        <DialogTitle sx={{ p: 0, fontSize: '16px' }}>{device.modelName || device.brand || 'Device'}</DialogTitle>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Stack>
      <DialogContent sx={{ pt: 1.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.75,
          }}
        >
          {facts.map(([label, value, span]) => (
            <Box key={label} sx={{ minWidth: 0, gridColumn: span === 'wide' ? { sm: '1 / -1' } : undefined }}>
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
              {/* `break-all` on the value: a device id is a 36-character uuid with no
                  spaces to break on, and it would otherwise push the grid wider. */}
              <Typography sx={{ fontSize: '13px', fontWeight: 500, wordBreak: 'break-all' }}>{value || '—'}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 2.5, pt: 1.5, borderTop: (t) => `1px solid ${hairline(t)}` }}>
          <SectionLabel sx={{ display: 'block', mb: 1 }}>Permissions</SectionLabel>
          {permissions.length === 0 ? (
            <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
              This install has never reported a permission snapshot.
            </Typography>
          ) : (
            <Stack spacing={0.75}>
              {/* Every key in the snapshot, whatever its status — `undetermined` and
                  `denied` are the ones being looked for, so filtering to the granted ones
                  would hide the answer. Sorted so the states that need action come first
                  and the list does not reorder itself between two devices. */}
              {permissions.map(([key, status]) => (
                <Stack key={key} direction="row" alignItems="baseline" spacing={1.5}>
                  <Typography sx={{ fontSize: '13px', flex: 1, minWidth: 0 }}>{humanizePermission(key)}</Typography>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: permissionColor(status) }}>
                    {status}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/** The title's own line height, so the icon beside it can be centred on it. */
const TITLE_LINE = '19px';

const DeviceRow = ({ device, first, onClick }) => {
  const f = useFormatters();
  const permissions = permissionEntries(device);
  // Notifications is pulled out because it is the one this section is opened for; the rest
  // are counted so the row says there is more without listing all of it.
  const others = permissions.filter(([key]) => key !== 'notifications' && key !== 'notification');
  const denied = others.filter(([, status]) => status === 'denied').map(([key]) => humanizePermission(key));

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      spacing={1.5}
      onClick={onClick}
      sx={{
        px: 1.75,
        py: 1,
        borderTop: first ? 'none' : (t) => `1px solid ${hairline(t)}`,
        cursor: 'pointer',
        '&:hover': { backgroundColor: (t) => softFill(t) },
      }}
    >
      {/* Centred on the TITLE, not on the row. The row is three lines tall, so centring
          against it put the glyph down beside the OS line; a fixed nudge (`mt: 0.3`) got
          close but drifts the moment the type scale moves. A box one title-line tall,
          centring its own content, lands on the title by construction. */}
      <Box sx={{ height: TITLE_LINE, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <PlatformIcon platform={device.platform} sx={{ fontSize: 16, color: 'text.secondary', display: 'block' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 500, lineHeight: TITLE_LINE }}>
          {device.modelName || device.brand || 'Unknown device'}
        </Typography>
        <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
          {[
            osSummary(device),
            device.appVersion && `app ${device.appVersion}`,
            device.isPhysical === false && 'simulator',
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
        {/* Tertiary line, and labelled. A bare timestamp in a column of its own read as
            the row's date the way a ride or a payment has one — but a device has no date,
            it has a last check-in, and "Seen at" is the difference. */}
        <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.15 }}>
          {device.lastSeenAt
            ? `Seen at ${f.date.toLocaleString(device.lastSeenAt, f.date.DateFormats.DATETIME_MED)}`
            : 'Never seen'}
          {/* Notifications is not the only permission on the snapshot, so anything else
              that is denied is named here — a rider who has revoked location is a separate
              conversation from one who has revoked push, and the row was hiding it. */}
          {denied.length > 0 && ` · ${denied.join(' and ')} denied`}
        </Typography>
      </Box>

      <Stack alignItems="flex-end" spacing={0.2} sx={{ flexShrink: 0 }}>
        <Typography
          sx={{
            fontSize: '11.5px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            // Red only for a refusal. A missing token or an unasked permission is a gap.
            color: device.pushEnabled
              ? 'success.main'
              : device.notificationPermission === 'denied'
                ? 'error.main'
                : 'text.disabled',
          }}
        >
          {device.pushEnabled
            ? 'Push on'
            : device.notificationPermission === 'denied'
              ? 'Push denied'
              : device.notificationPermission === 'granted'
                ? 'Push allowed, no token'
                : 'Push unknown'}
        </Typography>
        {permissions.length > 0 && (
          <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>
            {permissions.length} permission{permissions.length === 1 ? '' : 's'}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const UserDevicesCard = ({ user }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [opened, setOpened] = useState(null);
  // Bumped per request and by the effect's cleanup, so a response cannot land after
  // unmount or after `user.id` moved on.
  const request = useRef(0);

  const load = useCallback(async () => {
    const seq = (request.current += 1);
    setStatus('loading');
    try {
      const result = await api.users.getUserDevices({ id: user.id });
      if (seq !== request.current) return;
      setData(result);
      setStatus('done');
    } catch {
      if (seq === request.current) setStatus('failed');
    }
  }, [user.id]);

  useEffect(() => {
    load();
    return () => {
      request.current += 1;
    };
  }, [load]);

  const rows = data?.rows ?? [];

  return (
    <>
      <SectionCard
        title="Devices"
        icon={<PhoneIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
        meta={
          status === 'done' && data ? (
            <TrailingMeta>
              <Meta>
                {data.pushable} of {data.returned} accept push
              </Meta>
            </TrailingMeta>
          ) : null
        }
      >
        {status === 'loading' && <SectionSkeleton />}
        {status === 'failed' && <SectionError onRetry={load}>Couldn&rsquo;t load devices.</SectionError>}
        {status === 'done' && rows.length === 0 && <EmptyRows>No devices registered.</EmptyRows>}
        {status === 'done' && rows.length > 0 && (
          <Box sx={{ maxHeight: 320, ...scrollY }}>
            {rows.map((device, i) => (
              <DeviceRow key={device.deviceId} first={i === 0} device={device} onClick={() => setOpened(device)} />
            ))}
          </Box>
        )}
      </SectionCard>

      <DeviceDialog device={opened} onClose={() => setOpened(null)} />
    </>
  );
};

export default UserDevicesCard;
